// ---------------------------------------------------------------------------
// The browser side of EXPENSE CLAIMS. Sweep §4, migration 0190.
//
// SEPARATE FROM `pay.ts` AND `console.ts` FOR THE TRONC'S REASON (api/tronc.ts):
// a reimbursement is not pay and is taxed differently, and a module shared with
// pay is where somebody first adds the two together.
//
// NO ARITHMETIC ON MONEY HERE OR IN THE SCREENS. A mileage amount is distance ×
// rate, computed by the database; the event's totals arrive computed. A screen
// formats what it is given. Every state, kind and reason arrives with its label.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type ExpenseKind = { id: string; label: string; is_mileage: boolean; description: string };

export type MyExpenseClaim = {
  id: string;
  timesheet_id: string;
  event_name: string | null;
  role_name: string;
  shift_start: string;
  kind: string;
  kind_label: string;
  amount_minor: string;
  currency: string;
  distance: number | null;
  distance_unit: string | null;
  /** S85. The unit worded for the reader; null when there is no distance. */
  distance_unit_label: string | null;
  rate_minor: string | null;
  has_receipt: boolean;
  state: string;
  state_label: string;
  state_tone: string;
  rejection_reason_label: string | null;
  submitted_at: string;
  decided_at: string | null;
};

export type ClaimableShift = { id: string; event_name: string | null; role_name: string; shift_start: string };

export type Mileage = { unit: string; unit_label: string; rate_minor: string; currency: string } | null;

export type MyExpenses = {
  claims: MyExpenseClaim[];
  timesheets: ClaimableShift[];
  kinds: ExpenseKind[];
  mileage: Mileage;
};

export type EventExpenseClaim = {
  id: string;
  /** 0224. The claim's state still waits for somebody's decision. */
  awaits_decision: boolean;
  timesheet_id: string;
  worker_id: string;
  worker_name: string;
  role_name: string;
  shift_start: string;
  kind: string;
  kind_label: string;
  amount_minor: string;
  currency: string;
  distance: number | null;
  distance_unit: string | null;
  distance_unit_label: string | null;
  rate_minor: string | null;
  receipt_document_id: string | null;
  state: string;
  state_label: string;
  state_tone: string;
  rechargeable: boolean | null;
  rejection_reason: string | null;
  rejection_reason_label: string | null;
  submitted_at: string;
  decided_at: string | null;
  decided_by_you: boolean | null;
  /** S66. The live invoice that bills this claim to the client, if any. */
  invoice_reference: string | null;
  /** S66. The live payment batch that reimbursed it to the worker, if any. */
  reimbursement_reference: string | null;
};

/** Computed by app.event_expense_totals. Rechargeable is a subset of approved, never a sum with it. */
export type ExpenseTotal = {
  currency: string;
  approved_minor: string;
  rechargeable_minor: string;
  approved_count: string;
  waiting_count: string;
};

export type EventExpenses = {
  claims: EventExpenseClaim[];
  totals: ExpenseTotal[];
  reasons: { id: string; label: string; description: string }[];
  units: { id: string; label: string }[];
  mileage: { unit: string | null; rate_minor: string | null; currency: string | null; org_currency: string } | null;
  may_set_rate: boolean;
  may_open_receipts: boolean;
  /** S66. payment.run, and the enabled rail a reimbursement batch goes to. */
  may_reimburse: boolean;
  provider: string | null;
  /** 0224. The state rows an approval and a rejection land in. */
  approve_state: string | null;
  reject_state: string | null;
};

export const expenses = {
  mine: (base: string) => read(base, "/v1/my/expenses"),
  submit: (base: string, body: Record<string, unknown>) => mutate("POST", base, "/v1/my/expenses", body),
  forEvent: (base: string, eventId: string) => read(base, `/v1/events/${encodeURIComponent(eventId)}/expenses`),
  decide: (base: string, id: string, body: Record<string, unknown>) =>
    mutate("POST", base, `/v1/expenses/${encodeURIComponent(id)}/decision`, body),
  /** S144. The rate and the units without an event, for the Organisation settings screen. */
  mileageRate: (base: string) => read(base, "/v1/expenses/mileage-rate"),
  setMileageRate: (base: string, body: { unit: string | null; rate_minor: number | null }) =>
    mutate("POST", base, "/v1/expenses/mileage-rate", body),
  // S66 (0210). A REIMBURSEMENT IS A PAYMENT BATCH OF CLAIMS, NOT OF PAY. The same
  // route as wages, so there is one way money leaves; the lines are kinded by the
  // database and never enter gross or the payroll export.
  reimburse: (base: string, provider: string, claimIds: string[]) =>
    mutate("POST", base, "/v1/payment-batches", {
      provider,
      reference: `EXP-${Date.now()}`,
      expense_claims: claimIds,
    }),
};
